import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, FileDownload } from "@mui/icons-material";
import { useWarehouseLabelPdf, useWarehouseLabelPreview } from "hooks/useWarehouseLabel";
import {
  LabelPrintControls,
  LabelPrintHeader,
  LabelPrintProvider,
  LabelPrintStatus,
  withCopies,
} from "pages/WarehouseLabels/PrintPanel";
import { LOCATION_LABEL_LENGTH_IN, LOCATION_TAPE_WIDTH_IN, locationLabelPayload } from "utils/warehouseLabels";

// Preview, download, and VC-500W print for one batch of location codes.
export function LocationLabelBatchPreview({ codes }: { codes: string[] }) {
  const [page, setPage] = useState(0);
  const lastPage = Math.max(codes.length - 1, 0);
  const payload = useMemo(() => (codes.length ? locationLabelPayload(codes) : null), [codes]);
  const { imageUrl, error, isLoading } = useWarehouseLabelPreview(payload, page);
  const { downloadPdf, isLoadingPdf, pdfError } = useWarehouseLabelPdf();
  const current = codes[Math.min(page, lastPage)];

  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  return (
    <LabelPrintProvider kind="location" payload={payload}>
      <LabelPrintHeader />
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="overline" color="text.secondary">
            {codes.length
              ? `Label preview · ${LOCATION_LABEL_LENGTH_IN} × ${LOCATION_TAPE_WIDTH_IN} in`
              : "Label preview"}
          </Typography>
          {codes.length > 1 && (
            <Stack direction="row" alignItems="center">
              <IconButton
                type="button"
                aria-label="Previous label"
                disabled={page === 0}
                onClick={() => setPage((currentPage) => currentPage - 1)}
              >
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2">
                Label {Math.min(page, lastPage) + 1} of {codes.length}
              </Typography>
              <IconButton
                type="button"
                aria-label="Next label"
                disabled={page >= lastPage}
                onClick={() => setPage((currentPage) => currentPage + 1)}
              >
                <ChevronRight />
              </IconButton>
            </Stack>
          )}
        </Stack>
        <Box
          sx={{
            minHeight: 160,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {error && !isLoading ? (
            <Alert severity="warning">{error}</Alert>
          ) : imageUrl && current ? (
            <Box
              component="img"
              src={imageUrl}
              alt={`Label for ${current}`}
              sx={{ maxWidth: "100%", maxHeight: 240, bgcolor: "common.white", opacity: isLoading ? 0.5 : 1 }}
            />
          ) : isLoading ? (
            <CircularProgress size={32} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a location to see its label.
            </Typography>
          )}
        </Box>
        <LabelPrintControls>
          {(copies, overLimit) =>
            payload && (
              <Button
                type="button"
                variant="outlined"
                fullWidth
                startIcon={<FileDownload />}
                disabled={isLoadingPdf || overLimit}
                onClick={() => {
                  if (!overLimit) downloadPdf(withCopies(payload, copies));
                }}
              >
                {isLoadingPdf ? "Making PDF…" : "Download PDF"}
              </Button>
            )
          }
        </LabelPrintControls>
        {pdfError && <Alert severity="error">{pdfError}</Alert>}
        <LabelPrintStatus />
      </Stack>
    </LabelPrintProvider>
  );
}
